import React from "react";
import {
  ORDER_LIFECYCLE_STEPS,
  getLifecycleStepIndex,
} from "../../constants/orderLifecycle";
import { Check } from "@mui/icons-material";

/**
 * Reusable order lifecycle timeline.
 * Use in admin order tracking, customer tracking, business modules, etc.
 */
export default function OrderLifecycleTimeline({
  status,
  variant = "vertical",
  showDescription = true,
  className = "",
}) {
  const currentIndex = getLifecycleStepIndex(status);

  if (variant === "horizontal") {
    return (
      <div className={`w-full ${className}`}>
        <div className="flex items-center justify-between relative min-w-[640px]">
          {ORDER_LIFECYCLE_STEPS.map((step, index) => {
            const isComplete = currentIndex > index;
            const isActive = currentIndex === index;
            const isPending = currentIndex < index;

            return (
              <div
                key={step.key}
                className="relative flex flex-1 flex-col items-center"
              >
                {index < ORDER_LIFECYCLE_STEPS.length - 1 && (
                  <div
                    className={`absolute top-3 left-1/2 w-full h-0.5 z-0 ${
                      isComplete ? "bg-sky-600" : "bg-slate-200"
                    }`}
                  />
                )}
                <div
                  className={`w-6 h-6 rounded-full border-2 z-10 flex items-center justify-center ${
                    isComplete
                      ? "bg-sky-600 border-sky-600 text-white"
                      : isActive
                        ? "bg-white border-sky-600 ring-4 ring-sky-100"
                        : "bg-white border-slate-300"
                  }`}
                >
                  {isComplete && <Check sx={{ fontSize: 14 }} />}
                  {isActive && (
                    <span className="w-2.5 h-2.5 rounded-full bg-sky-600" />
                  )}
                </div>
                <span
                  className={`mt-2 text-xs font-semibold text-center px-1 ${
                    isComplete || isActive ? "text-sky-700" : "text-slate-400"
                  }`}
                >
                  {step.label}
                </span>
                {isPending && (
                  <span className="text-[10px] text-slate-400 mt-0.5">
                    Pending
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`}>
      <ol className="relative border-l-2 border-slate-200 ml-3 space-y-0">
        {ORDER_LIFECYCLE_STEPS.map((step, index) => {
          const isComplete = currentIndex > index;
          const isActive = currentIndex === index;
          const isPending = currentIndex < index;

          return (
            <li key={step.key} className="relative mb-6 ml-6 last:mb-0">
              <span
                className={`absolute -left-[13px] flex items-center justify-center w-6 h-6 rounded-full border-2 ${
                  isComplete
                    ? "bg-sky-600 border-sky-600 text-white"
                    : isActive
                      ? "bg-white border-sky-600 ring-4 ring-sky-100"
                      : "bg-white border-slate-300"
                }`}
              >
                {isComplete ? (
                  <Check sx={{ fontSize: 14 }} />
                ) : isActive ? (
                  <span className="w-2.5 h-2.5 rounded-full bg-sky-600" />
                ) : (
                  <span className="w-2 h-2 rounded-full bg-slate-300" />
                )}
              </span>
              <div
                className={`rounded-xl border px-4 py-3 ${
                  isActive
                    ? "border-sky-200 bg-sky-50"
                    : isComplete
                      ? "border-slate-100 bg-white"
                      : "border-slate-100 bg-slate-50"
                }`}
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <h4
                    className={`text-sm font-semibold ${
                      isActive
                        ? "text-sky-800"
                        : isComplete
                          ? "text-slate-800"
                          : "text-slate-500"
                    }`}
                  >
                    {step.label}
                  </h4>
                  <span
                    className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      isActive
                        ? "bg-sky-100 text-sky-700"
                        : isComplete
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    {isActive
                      ? "Current"
                      : isComplete
                        ? "Completed"
                        : "Pending"}
                  </span>
                </div>
                {showDescription && (
                  <p className="text-xs text-slate-500 mt-1">
                    {step.description}
                  </p>
                )}
                {isActive && status && (
                  <p className="text-xs font-medium text-sky-700 mt-2">
                    Status: {status}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
